import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  EditableColumnDef,
  EditableTable,
} from "../../../../../../../shared/components/table/inline-edit-table";
import { CustomSelect, SearchInput } from "../../../../../../../shared/components/inputfields/form-elements";
import { StyledButton } from "../../../../../../../shared/components/buttons/custom-button";
import { ITcProductFirstProcessorInformation } from "../../../../tc-models/tc-req-dto/tc-certified-product-details.req_dto";
import { useFormActions } from "../../../../../../../shared/hooks/form-builder.hooks";
import { dtrackItService } from "../../../../tc-service/dtrackit.service";
import useNotification from "../../../../../../../shared/hooks/useNotification";
import { useTransactionCertificateStore } from "../../../../../../../store/transaction-certificates.store";
import { Utills } from "../../../../../../../shared/app-configs/utilit";
import { buyerSellerDetailsService } from "../../../../tc-service/buyer-seller-details.service";
import { useParams } from "react-router-dom";
import { useApi } from "../../../../../../../shared/hooks/useApi";
import { TCAppConfig } from "../../../../../../../shared/app-configs/tc-app-configs";

interface Option {
  value: string;
  label: string;
}

interface ProductFirstProcessorInfoProps {
  formRef: React.MutableRefObject<any>;
  ProductFirstProcessData: ITcProductFirstProcessorInformation[];
  setProductFirstProcessInfoData: React.Dispatch<
    React.SetStateAction<ITcProductFirstProcessorInformation[]>
  >;
  facilityProcessCodes?: string[]; // Optional: facility codes to check trigger list
}

interface TEDetail {
  TE_ID: string;
  account_name: string;
  street?: string;
  city?: string;
  postal_code?: string;
  country?: string;
  state?: string;
}

const ProductFirstProcessorInfo: React.FC<ProductFirstProcessorInfoProps> = ({
  formRef,
  ProductFirstProcessData,
  setProductFirstProcessInfoData,
  facilityProcessCodes,
}) => {
  const { notify } = useNotification();
  const { updateField } = useFormActions("certified-products");
  const { setRestrictPopUpSave, setRawMtrlCodes } = useTransactionCertificateStore();
  const { TE_ID } = useParams();
  const { executeGet } = useApi();

  const [editingRowIndex, setEditingRowIndex] = useState<number>(-1);
  const [data, setData] = useState<ITcProductFirstProcessorInformation[]>(
    Array.isArray(ProductFirstProcessData) ? ProductFirstProcessData : []
  );
  const [rawMaterials, setRawMaterials] = useState<Option[]>([]);
  const [allRawMaterials, setAllRawMaterials] = useState<Option[]>([]);
  const [hasGRSorRCS, setHasGRSorRCS] = useState(false);
  const [isMandatory, setIsMandatory] = useState(false);

  const validationSchema = Yup.object().shape({
    // Make fields optional here; parent-level validation enforces conditional requirements
    tcProductFirstProcessorTeId: Yup.string(),
    tcProductFirstProcessCode: Yup.string(),
  });

  const formik = useFormik<ITcProductFirstProcessorInformation>({
    initialValues: {
      tcProductFirstProcessorTeId: "",
      tcProductFirstProcessCode: "",
    },
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: () => {},
  });

  useEffect(() => {
    if (formRef) {
      formRef.current = {
        ...formik,
        editingRowIndex,
        hasUnsavedRow: editingRowIndex !== -1, // Track if a row is being edited
        isMandatory, // Expose mandatory status to parent
      };
      console.log("ProductFirstProcessorInfo - formRef updated:", formRef.current); // Debug log
    }
  }, [formRef, formik, editingRowIndex, isMandatory]);

  useEffect(() => {
    setData(Array.isArray(ProductFirstProcessData) ? ProductFirstProcessData : []);
  }, [ProductFirstProcessData]);

  useEffect(() => {
    const fetchTcBaseDetails = async () => {
      if (!TE_ID) return;
      try {
        const response = await executeGet(TCAppConfig.GET_TC_BASE_DETAILS_V4, { tcId: TE_ID });
        const data = response?.data;
        const standards = data?.tcStandard || [];
        const hasGRSorRCS = standards.includes("GRS") || standards.includes("RCS");
        setHasGRSorRCS(hasGRSorRCS);
      } catch (err) {
        console.error("Failed to fetch TC base details:", err);
      }
    };
   fetchTcBaseDetails()  
  },[])

  useEffect(() => {
    const getFacilityProcessCategoryCodes = async () => {
      try {
        const result: any = await dtrackItService.getFacilityProcessCategoryCodes();
        const codes = result.data.map((t: any) => ({
          value: t.code_1,
          label: `${t.description_1} (${t.code_1})`,
        }));
        setRawMaterials(codes);
      } catch (err) {
        Utills.cl(`Error fetching raw material codes: ${err}`);
        notify("Failed to fetch raw material codes", "error");
      }
    };
    getFacilityProcessCategoryCodes();
  }, [setRawMaterials]);


  // Compute whether first-processor fields are mandatory based on validation conditionsS
  useEffect(() => {
    const triggerSet = new Set([
      'PR0043', 'PR0003', 'PR0004', 'PR0042', 'PR0005', 'PR0006', 'PR0017', 'PR0044', 'PR0024', 'PR0047'
    ]);

    const facilityTriggers = Array.isArray(facilityProcessCodes)
      ? facilityProcessCodes.some(code => triggerSet.has(String(code)))
      : false;

    // Mandatory ONLY when BOTH conditions are met: GRS/RCS AND trigger PR codes
    setIsMandatory(hasGRSorRCS && facilityTriggers);
  }, [hasGRSorRCS, facilityProcessCodes]);

  const handleCellChange = (rowIndex: number, columnId: string, value: any) => {
    formik.setFieldValue(columnId, value);
    setData(prev => prev.map((row, i) => (i === rowIndex ? { ...row, [columnId]: value } : row)));
  };

  const handleAddNewRow = () => {
    const newRow: ITcProductFirstProcessorInformation = {
      tcProductFirstProcessorTeId: "",
      tcProductFirstProcessCode: "",
    };
    const newData = [...data, newRow];
    setData(newData);
    setProductFirstProcessInfoData(newData);
    setEditingRowIndex(newData.length - 1);
    setRestrictPopUpSave(true);
    formik.setValues(newRow);
    formik.setTouched({});
  };

  const handleCancelEdit = () => {
    const newData = editingRowIndex === data.length - 1 ? data.slice(0, -1) : [...data];
    setData(newData);
    setProductFirstProcessInfoData(newData);
    try {
      updateField("productFirstProcessorInfo", newData); // Update global store
    } catch (error) {
      notify("Failed to update data on cancel", "error");
      console.error("Error updating data on cancel:", error);
    }
    setEditingRowIndex(-1);
    setRestrictPopUpSave(false);
    formik.resetForm();
    (formik as any).hasUnsavedRow = false; // Reset unsaved state
  };

  const handleSaveRow = async () => {
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      Object.values(errors).forEach(err => notify(err as string, "error"));
      return;
    }
  
    const updatedRow = formik.values;
    
    // Validate that at least one field is filled before saving
    const teId = String(updatedRow.tcProductFirstProcessorTeId || "").trim();
    const processCode = String(updatedRow.tcProductFirstProcessCode || "").trim();
    
    if (!teId && !processCode) {
      notify("At least one field (TE-ID or Process Code) is required to save a row", "error");
      return;
    }
    
    const newData = data.map((row, i) => (i === editingRowIndex ? updatedRow : row));
    
    // Filter out any completely empty rows (rows with neither field filled)
    const filteredData = newData.filter(row => {
      const rowTeId = String(row.tcProductFirstProcessorTeId || "").trim();
      const rowProcessCode = String(row.tcProductFirstProcessCode || "").trim();
      return rowTeId || rowProcessCode; // Keep if at least one field has a value
    });
    
    setData(filteredData);
    setProductFirstProcessInfoData(filteredData);
    setEditingRowIndex(-1);
    setRestrictPopUpSave(false);
    formik.resetForm();
    (formik as any).hasUnsavedRow = false; // Explicitly reset unsaved state
  
    try {
      updateField("productFirstProcessorInfo", filteredData);
      notify("Row saved successfully", "success");
    } catch (error) {
      notify("Failed to save row", "error");
      console.error("Error saving row:", error);
    }
  };

  const handleEditRow = (rowIndex: number) => {
    setEditingRowIndex(rowIndex);
    formik.setValues(data[rowIndex] || formik.initialValues);
    setRestrictPopUpSave(true);
  };

  const handleDeleteRow = (rowIndex: number) => {
    const newData = data.filter((_, i) => i !== rowIndex);
    setData(newData);
    setProductFirstProcessInfoData(newData);
    try {
      updateField("productFirstProcessorInfo", newData);
      notify("Row deleted successfully", "success");
    } catch (error) {
      notify("Failed to delete row", "error");
      console.error("Error deleting row:", error);
    }
  };

  const fetchTEDetailsFromAPI = async (
    query: string,
    offset: number,
    type: string
  ): Promise<TEDetail[]> => {
    try {
      const queryParams: Record<string, string | number> = {
        query_param: type === "query" ? query : "",
        offset: offset,
        name: type === "name" ? query : "",
      };
      const response = await buyerSellerDetailsService.getTEDetails(queryParams);
      return response.data || [];
    } catch (error) {
      console.error("Error fetching TE details:", error);
      notify("Error fetching TE details", "error");
      return [];
    }
  };

  const columns: EditableColumnDef<ITcProductFirstProcessorInformation>[] = [
    {
      accessorKey: "tcProductFirstProcessorTeId",
      header: "Product First Processor TE-ID",
      Header: (
        <span>
          {isMandatory && <i style={{ color: "red" }}>*</i>} Product First Processor TE-ID
        </span>
      ),
      renderEditCell: ({ value, onChange }) => (
        <SearchInput
          pageName="Product First-Processor Info"
          value={value}
          onChange={(selected: { [key: string]: any } | null) => {
            onChange(selected ? (selected as TEDetail).TE_ID : "");
          }}
          onSearch={(query: string, offset: number) => fetchTEDetailsFromAPI(query, offset, "query")}
          displayKey="TE_ID"
          placeholder="Search First Proccessor TE-ID"
          restrictInputFormat={true}
          fullWidth={true}
          size="small"
        />
      ),
    },
    {
      accessorKey: "tcProductFirstProcessCode",
      header: "Product First Process Code",
      Header: (
        <span>
          {isMandatory && <i style={{ color: "red" }}>*</i>} Product First Process Code
        </span>
      ),
      renderEditCell: ({ value, onChange }) => (
        <CustomSelect
          value={value}
          options={rawMaterials}
          enableSearch
          onChange={e => onChange(e.target.value)}
          placeholder="Select First Process Code"
        />
      ),
    },
  ];

  return (
    <Box
      component="main"
      sx={{ flexGrow: 1, p: 1, border: "2px solid rgba(128, 128, 128, .2)", my: 4 }}
    >
      <Box display="flex" mb={3} justifyContent="flex-end" width="100%">
        <StyledButton
          disabled={editingRowIndex !== -1}
          onClick={handleAddNewRow}
          sx={{ mb: 2 }}
        >
          Add Product First Processor Info
        </StyledButton>
      </Box>

      <EditableTable
        data={data.filter(row => row && Object.keys(row).length > 0)}
        columns={columns}
        onSaveRow={handleSaveRow}
        onCellChange={handleCellChange}
        editingRowIndex={editingRowIndex}
        onEditRow={handleEditRow}
        onCancelEdit={handleCancelEdit}
        onDeleteRow={handleDeleteRow}
      />
    </Box>
  );
};

export default ProductFirstProcessorInfo;
